import AddIcon from '@mui/icons-material/Add';
import DeleteOutlineIcon from '@mui/icons-material/DeleteOutline';
import EditOutlinedIcon from '@mui/icons-material/EditOutlined';
import {
  Box,
  Button,
  IconButton,
  Paper,
  Stack,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Tooltip,
  Typography,
} from '@mui/material';
import React, { useMemo, useState } from 'react';
import toast from 'react-hot-toast';
import { useMutation, useQuery, useQueryClient } from 'react-query';
import LoaderProgressComponent from '../../../Components/menu_planner/LoaderProgressComponent';
import {
  getAllMealsData,
  patchMealAssignedTags,
  reloadMeal,
} from '../food_box/create_food_box/CreateFoodBoxApi';
import {
  buildTagRemovalMealUpdates,
  getMealsUsingTag,
} from '../../../utils/mealTagUtils';
import { queryKeys } from '../../../utils/queryKeys';
import DeleteTagDialog from './DeleteTagDialog';
import {
  createTag,
  deleteTag,
  getAllTags,
  updateTag,
} from './TagManagementApi';
import TagManagementDialog from './TagManagementDialog';

const getTagErrorMessage = (error, fallbackMessage) => {
  return error?.response?.data?.message ?? fallbackMessage;
};

const sortTags = (tags = []) => {
  return [...tags].sort((firstTag, secondTag) => {
    const sequenceDifference =
      (firstTag?.displayOrder ?? 0) - (secondTag?.displayOrder ?? 0);

    if (sequenceDifference !== 0) {
      return sequenceDifference;
    }

    return (firstTag?.nameEn ?? '').localeCompare(secondTag?.nameEn ?? '');
  });
};

const TagManagementScreen = () => {
  const queryClient = useQueryClient();
  const [isTagDialogOpen, setIsTagDialogOpen] = useState(false);
  const [editingTag, setEditingTag] = useState(null);
  const [tagToDelete, setTagToDelete] = useState(null);
  const [isDeletingTag, setIsDeletingTag] = useState(false);

  const { data, isLoading, isError } = useQuery(
    queryKeys.getAllTagsData,
    getAllTags,
    {
      refetchOnWindowFocus: false,
    }
  );

  const { data: mealsData, isLoading: isLoadingMealUsage } = useQuery(
    queryKeys.getMealData,
    getAllMealsData,
    {
      enabled: !!tagToDelete,
      refetchOnWindowFocus: false,
    }
  );

  const orderedTags = useMemo(() => sortTags(data?.data ?? []), [data?.data]);
  const affectedMeals = useMemo(
    () => getMealsUsingTag(mealsData?.data ?? [], tagToDelete?.id),
    [mealsData?.data, tagToDelete?.id]
  );
  const nextDisplayOrder = useMemo(() => {
    if (!orderedTags.length) {
      return 1;
    }

    return Math.max(...orderedTags.map((tag) => tag.displayOrder ?? 0)) + 1;
  }, [orderedTags]);

  const invalidateTags = () => {
    return queryClient.invalidateQueries(queryKeys.getAllTagsData);
  };

  const createTagMutation = useMutation(createTag, {
    onSuccess: async () => {
      await invalidateTags();
      toast.success('Tag created successfully');
      setIsTagDialogOpen(false);
    },
    onError: (error) => {
      toast.error(getTagErrorMessage(error, 'Unable to create tag'));
    },
  });

  const updateTagMutation = useMutation(updateTag, {
    onSuccess: async () => {
      await invalidateTags();
      toast.success('Tag updated successfully');
      setIsTagDialogOpen(false);
    },
    onError: (error) => {
      toast.error(getTagErrorMessage(error, 'Unable to update tag'));
    },
  });

  const deleteTagMutation = useMutation(deleteTag, {
    onSuccess: async () => {
      await Promise.all([
        invalidateTags(),
        queryClient.invalidateQueries(queryKeys.getMealData),
      ]);
      toast.success('Tag deleted successfully');
      setTagToDelete(null);
    },
    onError: (error) => {
      toast.error(getTagErrorMessage(error, 'Unable to delete tag'));
    },
  });

  const handleAddNewTagClick = () => {
    setEditingTag(null);
    setIsTagDialogOpen(true);
  };

  const handleEditTagClick = (tag) => {
    setEditingTag(tag);
    setIsTagDialogOpen(true);
  };

  const handleCloseTagDialog = () => {
    if (createTagMutation.isLoading || updateTagMutation.isLoading) {
      return;
    }

    setEditingTag(null);
    setIsTagDialogOpen(false);
  };

  const handleTagSubmit = async (payload) => {
    if (editingTag?.id) {
      await updateTagMutation.mutateAsync({
        id: editingTag.id,
        payload,
      });
      return;
    }

    await createTagMutation.mutateAsync(payload);
  };

  const handleDeleteTag = async () => {
    if (!tagToDelete?.id || isDeletingTag) {
      return;
    }

    setIsDeletingTag(true);

    try {
      const mealsResponse = await queryClient.fetchQuery(
        queryKeys.getMealData,
        getAllMealsData
      );
      const mealUpdates = buildTagRemovalMealUpdates(
        mealsResponse?.data ?? [],
        tagToDelete.id
      );

      for (const mealUpdate of mealUpdates) {
        await patchMealAssignedTags(mealUpdate);
        await reloadMeal(
          queryClient,
          mealUpdate.id ?? mealUpdate.externalId
        );
      }
    } catch (error) {
      toast.error(
        getTagErrorMessage(error, 'Unable to remove this tag from food boxes')
      );
      setIsDeletingTag(false);
      return;
    }

    try {
      await deleteTagMutation.mutateAsync(tagToDelete.id);
    } catch (error) {
      // Mutation-level error handling already shows the toast.
    } finally {
      setIsDeletingTag(false);
    }
  };

  return (
    <>
      <Stack
        direction={{ xs: 'column', md: 'row' }}
        justifyContent='flex-end'
        alignItems={{ xs: 'flex-start', md: 'center' }}
        gap={2}
      >
        <Button variant='contained' startIcon={<AddIcon />} onClick={handleAddNewTagClick}>
          Add new tag
        </Button>
      </Stack>

      {isLoading ? (
        <LoaderProgressComponent />
      ) : isError ? (
        <Paper sx={{ p: 4, borderRadius: '16px' }}>
          <Typography variant='s16_w500'>
            Unable to load tags right now.
          </Typography>
        </Paper>
      ) : (
        <TableContainer
          component={Paper}
          sx={{
            border: '1px solid #E5E7EB',
            borderRadius: '16px',
            boxShadow: 'none',
            overflow: 'hidden',
          }}
        >
          <Table>
            <TableHead>
              <TableRow>
                <TableCell sx={{ fontWeight: 700, width: '14rem' }}>↕ Seq</TableCell>
                <TableCell sx={{ fontWeight: 700 }}>English</TableCell>
                <TableCell sx={{ fontWeight: 700 }}>Norwegian</TableCell>
                <TableCell sx={{ fontWeight: 700, width: '12rem' }} align='right'>
                  Actions
                </TableCell>
              </TableRow>
            </TableHead>
            <TableBody>
              {orderedTags.length > 0 ? (
                orderedTags.map((tag) => {
                  return (
                    <TableRow key={tag.id} hover>
                      <TableCell>{tag.displayOrder ?? 0}</TableCell>
                      <TableCell>{tag.nameEn}</TableCell>
                      <TableCell>{tag.nameNo}</TableCell>
                      <TableCell align='right'>
                        <Tooltip title='Edit'>
                          <IconButton onClick={() => handleEditTagClick(tag)}>
                            <EditOutlinedIcon />
                          </IconButton>
                        </Tooltip>
                        <Tooltip title='Delete'>
                          <IconButton
                            color='error'
                            onClick={() => setTagToDelete(tag)}
                          >
                            <DeleteOutlineIcon />
                          </IconButton>
                        </Tooltip>
                      </TableCell>
                    </TableRow>
                  );
                })
              ) : (
                <TableRow>
                  <TableCell colSpan={4}>
                    <Box py={8} textAlign='center'>
                      <Typography variant='s16_w500' color='#6B7280'>
                        No tags yet. Click "Add new tag" to create one.
                      </Typography>
                    </Box>
                  </TableCell>
                </TableRow>
              )}
            </TableBody>
          </Table>
        </TableContainer>
      )}

      <TagManagementDialog
        open={isTagDialogOpen}
        onClose={handleCloseTagDialog}
        onSubmit={handleTagSubmit}
        tag={editingTag}
        defaultDisplayOrder={nextDisplayOrder}
        isSubmitting={createTagMutation.isLoading || updateTagMutation.isLoading}
      />

      <DeleteTagDialog
        open={!!tagToDelete}
        onClose={() => {
          if (!isDeletingTag) {
            setTagToDelete(null);
          }
        }}
        onConfirm={handleDeleteTag}
        tag={tagToDelete}
        isDeleting={isDeletingTag}
        affectedMealCount={affectedMeals?.length ?? 0}
        isLoadingAffectedMeals={isLoadingMealUsage}
      />
    </>
  );
};

export default TagManagementScreen;
